import { RootState, ThunkDispatch } from "core-integration/src/store";
import {
  getH2HLeague,
  getH2HNewEntries,
} from "core-integration/src/store/leagues/reducers";
import { fetchH2HLeagueStandings } from "core-integration/src/store/leagues/thunks";
import { Button } from "plos/src/components/buttons/Button";
import Link from "plos/src/components/links/Link";
import Subheading from "plos/src/components/Subheading";
import { SurfaceContainer } from "plos/src/components/SurfaceContainer";
import { Cell, Column, Row, Table } from "plos/src/components/Table";
import { rightSidebarLayout } from "plos/src/layouts";
import { fixedTable } from "plos/src/styles/utils.css";
import { useEffect, useState } from "react";
import { TableBody, TableHeader } from "react-aria-components";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import { HelmetHead } from "../HelmetHead";
import { ControlArrowLeft, ControlArrowRight } from "../icons/Arrows";
import ReportNameButton from "../ReportNameButton";
import {
  newEntriesTableCell,
  newEntriesTableCol,
} from "./Classic/StandingsClassic/NewEntriesClassicTable/newEntriesClassicTable.css";
import { standingsMainContent } from "./Classic/StandingsClassic/standingsClassic.css";
import { reportButtonWrapper, StandingsHeading } from "./shared";
import { pager } from "./shared/styles/leaguesTableStyles.css";

const NewEntriesH2H = () => {
  const { t } = useTranslation("leaguesAndCups");
  const { leagueId } = useParams();
  const reduxDispatch = useDispatch<ThunkDispatch>();
  const leagueNumber = Number(leagueId);
  const [page, setPage] = useState(1);

  const league = useSelector((state: RootState) =>
    getH2HLeague(state, leagueNumber),
  );
  const newEntries = useSelector((state: RootState) =>
    getH2HNewEntries(state, leagueNumber, page),
  );
  useEffect(() => {
    reduxDispatch(fetchH2HLeagueStandings(leagueNumber, page, 1));
  }, [reduxDispatch, page, leagueNumber]);

  if (!league || !newEntries) {
    return null;
  }

  return (
    <>
      <HelmetHead
        title={t(
          "newEntriesH2H.metadata.title",
          "{{leagueName}} - New Entries | Fantasy Premier League",
          {
            leagueName: league.name,
          },
        )}
        description={t(
          "newEntriesH2H.metadata.description",
          "To view the {{leagueName}} invitational league new entries, as well as creating & joining new leagues, visit the official website of the Premier League.",
          {
            leagueName: league.name,
          },
        )}
      />
      <div className={rightSidebarLayout}>
        <SurfaceContainer>
          <StandingsHeading
            league={league}
            leagueNumber={leagueNumber}
            type="h2h"
          />
          <div className={standingsMainContent}>
            <Subheading>{t("newEntries", "New entries")}</Subheading>

            <Table
              className={fixedTable}
              aria-label={t("newEntries", "New entries")}
            >
              <TableHeader>
                <Column className={newEntriesTableCol}>
                  {t("common:team", "Team")}
                </Column>
                <Column isRowHeader className={newEntriesTableCol}>
                  {t("common:manager", "Manager")}
                </Column>
              </TableHeader>
              <TableBody>
                {newEntries.results.map((ne) => (
                  <Row key={ne.entry}>
                    <Cell className={newEntriesTableCell}>{ne.entry_name}</Cell>
                    <Cell className={newEntriesTableCell}>
                      <Link to={`/entry/${ne.entry}/history`}>
                        {ne.player_first_name} {ne.player_last_name}
                      </Link>
                    </Cell>
                  </Row>
                ))}
              </TableBody>
            </Table>

            <div className={pager}>
              {page > 1 && (
                <Button appearance="tonal" onPress={() => setPage(page - 1)}>
                  <ControlArrowLeft />
                  <span>{t("pagination.previous", "Previous")}</span>
                </Button>
              )}
              {newEntries.has_next && (
                <Button appearance="tonal" onPress={() => setPage(page + 1)}>
                  <span>{t("pagination.next", "Next")}</span>
                  <ControlArrowRight />
                </Button>
              )}
            </div>
            <div className={reportButtonWrapper.mobile}>
              <ReportNameButton league={league} />
            </div>
          </div>
        </SurfaceContainer>
      </div>
    </>
  );
};

export default NewEntriesH2H;
